const DB_NAME = "campushubai-offline";
const DB_VERSION = 2;
const OUTBOX_STORE = "mutation-outbox";
const FALLBACK_KEY = "campushubai-offline-outbox";
// Mutations the server permanently refused (e.g. a student was withdrawn in the
// gap between an offline mark and its sync). These will never succeed on
// replay, so we pull them out of the pending queue and park them here instead —
// visible + actionable to the operator, never silently lost, never clogging the
// pending count forever. Small volume + human-reviewed → localStorage is fine.
const REJECTED_KEY = "campushubai-offline-rejected";

export type OfflineMutationMethod = "POST" | "PATCH" | "PUT" | "DELETE";
export type OfflineMutationStatus = "pending" | "syncing" | "failed";

export interface OfflineMutationEntry {
  id: string;
  method: OfflineMutationMethod;
  url: string;
  data?: unknown;
  userId?: string | null;
  schoolId?: string | null;
  createdAt: number;
  updatedAt: number;
  attempts: number;
  status: OfflineMutationStatus;
  error?: string;
}

export interface QueueOfflineMutationInput {
  method: OfflineMutationMethod;
  url: string;
  data?: unknown;
  userId?: string | null;
  schoolId?: string | null;
}

export interface OfflineOutboxChangeDetail {
  pendingCount: number;
}

const hasIndexedDb = typeof indexedDB !== "undefined";
let dbPromise: Promise<IDBDatabase> | null = null;

function createId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `offline-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function openOutboxDb(): Promise<IDBDatabase> {
  if (!hasIndexedDb) {
    return Promise.reject(new Error("IndexedDB is not available"));
  }

  if (dbPromise) {
    return dbPromise;
  }

  const promise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    // Hard timeout: in some environments (a stuck versionchange, a WebView
    // that never fires success/error/blocked) indexedDB.open settles with
    // NONE of its events, so awaiting it hangs forever — which froze the
    // offline "Save Attendance" tap on a never-resolving spinner with no
    // toast and no queued row. Bound the open so a wedged DB rejects instead,
    // letting queueOfflineMutation fall back to localStorage and resolve.
    let settled = false;
    const finish = (fn: () => void) => { if (settled) return; settled = true; clearTimeout(timer); fn(); };
    const timer = setTimeout(
      () => finish(() => reject(new Error("Offline outbox open timed out"))),
      3000,
    );

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("api-get-cache")) {
        db.createObjectStore("api-get-cache", { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        db.createObjectStore(OUTBOX_STORE, { keyPath: "id" });
      }
    };

    request.onsuccess = () => finish(() => resolve(request.result));
    request.onerror = () => finish(() => reject(request.error ?? new Error("Unable to open offline outbox")));
    request.onblocked = () => finish(() => reject(new Error("Offline outbox upgrade was blocked")));
  });

  dbPromise = promise.catch((error: unknown) => {
    dbPromise = null;
    throw error;
  });

  return dbPromise;
}

async function readIndexedDb(): Promise<OfflineMutationEntry[]> {
  const db = await openOutboxDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OUTBOX_STORE, "readonly");
    const request = tx.objectStore(OUTBOX_STORE).getAll();
    request.onsuccess = () => resolve((request.result as OfflineMutationEntry[]).sort((a, b) => a.createdAt - b.createdAt));
    request.onerror = () => reject(request.error ?? new Error("Unable to read offline outbox"));
  });
}

async function putIndexedDb(entry: OfflineMutationEntry): Promise<void> {
  const db = await openOutboxDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(OUTBOX_STORE, "readwrite");
    tx.objectStore(OUTBOX_STORE).put(entry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("Unable to write offline outbox"));
  });
}

async function deleteIndexedDb(id: string): Promise<void> {
  const db = await openOutboxDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(OUTBOX_STORE, "readwrite");
    tx.objectStore(OUTBOX_STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("Unable to delete offline mutation"));
  });
}

function readFallback(): OfflineMutationEntry[] {
  const raw = localStorage.getItem(FALLBACK_KEY);
  if (!raw) return [];
  return JSON.parse(raw) as OfflineMutationEntry[];
}

function writeFallback(entries: OfflineMutationEntry[]): void {
  localStorage.setItem(FALLBACK_KEY, JSON.stringify(entries));
}

function notifyOutboxChanged(count: number): void {
  window.dispatchEvent(
    new CustomEvent<OfflineOutboxChangeDetail>("campushub:offline-outbox-changed", {
      detail: { pendingCount: count },
    }),
  );
}

export async function listOfflineMutations(): Promise<OfflineMutationEntry[]> {
  try {
    return await readIndexedDb();
  } catch {
    try {
      return readFallback().sort((a, b) => a.createdAt - b.createdAt);
    } catch {
      return [];
    }
  }
}

export async function getOfflineMutationCount(): Promise<number> {
  return (await listOfflineMutations()).length;
}

export async function queueOfflineMutation(input: QueueOfflineMutationInput): Promise<OfflineMutationEntry> {
  const now = Date.now();
  const entry: OfflineMutationEntry = {
    id: createId(),
    method: input.method,
    url: input.url,
    data: input.data,
    userId: input.userId ?? null,
    schoolId: input.schoolId ?? null,
    createdAt: now,
    updatedAt: now,
    attempts: 0,
    status: "pending",
  };

  try {
    await putIndexedDb(entry);
  } catch {
    const entries = readFallback();
    entries.push(entry);
    writeFallback(entries);
  }

  notifyOutboxChanged(await getOfflineMutationCount());
  return entry;
}

export async function markOfflineMutationFailed(id: string, error: string): Promise<void> {
  const entries = await listOfflineMutations();
  const entry = entries.find((item) => item.id === id);
  if (!entry) return;

  const updated: OfflineMutationEntry = {
    ...entry,
    attempts: entry.attempts + 1,
    status: "failed",
    error,
    updatedAt: Date.now(),
  };

  try {
    await putIndexedDb(updated);
  } catch {
    writeFallback(entries.map((item) => (item.id === id ? updated : item)));
  }

  notifyOutboxChanged(await getOfflineMutationCount());
}

/**
 * Wipe every queued mutation — called on logout (CAMP P1 module audit,
 * shared-device safety), alongside `clearAllOfflineCache()`. Without this a
 * mutation queued offline by one user on a shared device (kiosk, front-desk
 * PC, driver phone) sits in the SAME IndexedDB store the next logged-in user
 * shares, and `useOfflineSync` would otherwise be the only thing standing
 * between it and replaying under the wrong session — this removes the entry
 * before that check ever has to run.
 */
export async function clearAllOfflineMutations(): Promise<void> {
  try {
    const db = await openOutboxDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(OUTBOX_STORE, "readwrite");
      tx.objectStore(OUTBOX_STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("Unable to clear offline outbox"));
    });
  } catch {
    // Fall through to the localStorage fallback sweep below regardless.
  }
  try {
    localStorage.removeItem(FALLBACK_KEY);
  } catch {
    // Best-effort — a logout must never throw and block the redirect.
  }
  notifyOutboxChanged(0);
}

export async function removeOfflineMutation(id: string): Promise<void> {
  try {
    await deleteIndexedDb(id);
  } catch {
    writeFallback(readFallback().filter((entry) => entry.id !== id));
  }

  notifyOutboxChanged(await getOfflineMutationCount());
}

// ── Rejected mutations (permanently refused by the server) ───────────────────

export interface RejectedMutationEntry extends OfflineMutationEntry {
  reason: string;
  rejectedAt: number;
}

export interface OfflineRejectedChangeDetail {
  rejectedCount: number;
}

function notifyRejectedChanged(count: number): void {
  window.dispatchEvent(
    new CustomEvent<OfflineRejectedChangeDetail>("campushub:offline-rejected-changed", {
      detail: { rejectedCount: count },
    }),
  );
}

export function listRejectedMutations(): RejectedMutationEntry[] {
  try {
    const raw = localStorage.getItem(REJECTED_KEY);
    if (!raw) return [];
    return (JSON.parse(raw) as RejectedMutationEntry[]).sort((a, b) => b.rejectedAt - a.rejectedAt);
  } catch {
    return [];
  }
}

/** Park a permanently-rejected mutation for operator review. */
export async function recordRejectedMutation(entry: OfflineMutationEntry, reason: string): Promise<void> {
  const rejected: RejectedMutationEntry = { ...entry, reason, rejectedAt: Date.now() };
  try {
    const list = listRejectedMutations().filter((r) => r.id !== entry.id);
    // Cap the list so a pathological loop can't grow localStorage unbounded.
    list.unshift(rejected);
    localStorage.setItem(REJECTED_KEY, JSON.stringify(list.slice(0, 100)));
  } catch {
    // Non-fatal — the entry is still removed from the pending queue.
  }
  notifyRejectedChanged(listRejectedMutations().length);
}

export function clearRejectedMutation(id: string): void {
  try {
    localStorage.setItem(REJECTED_KEY, JSON.stringify(listRejectedMutations().filter((r) => r.id !== id)));
  } catch {
    // ignore
  }
  notifyRejectedChanged(listRejectedMutations().length);
}

export function clearAllRejectedMutations(): void {
  try {
    localStorage.removeItem(REJECTED_KEY);
  } catch {
    // ignore
  }
  notifyRejectedChanged(0);
}

